/**
 * Unified Feature Flag Hook - PostHog Only
 *
 * React hooks for accessing PostHog feature flags in components.
 *
 * @example
 * ```typescript
 * import { useFeatureFlag, useFeatureFlags } from '@client/hooks/useFeatureFlag';
 *
 * function MyComponent() {
 *   // Single flag
 *   const enableNewFeature = useFeatureFlag('enableNewFeature');
 *
 *   // Multiple flags
 *   const { enableAi, enableDebug } = useFeatureFlags(['enableAi', 'enableDebug']);
 *
 *   // With custom default
 *   const maxRetries = useFeatureFlag('maxRetries', 3);
 *
 *   if (enableNewFeature) {
 *     return <NewFeature />;
 *   }
 *   return <OldFeature />;
 * }
 * ```
 */

// Slim React entry: matches the slim SDK import in `posthogClient.ts`.
// Avoids pulling in the full `posthog-js` bundle alongside the slim one.
import { getFeatureFlagReadiness, subscribeFeatureFlagReadiness } from '@client/utils/feature-flags/posthogClient';
import { usePostHog, useFeatureFlagVariantKey as usePostHogFeatureFlagVariantKey } from 'posthog-js/react/slim';
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';

type InvalidTrueFeatureFlagDefault = true & {
    readonly __featureFlagDefaultMustNotBeTrue: 'Use false or omit the default for boolean flags';
};

/**
 * Hook to get a single feature flag value
 *
 * @param flagKey - The key of the feature flag
 * @param defaultValue - The default value if the flag is not found (default: false)
 * @returns The feature flag value
 *
 * @example
 * ```typescript
 * const enableNewFeature = useFeatureFlag('enableNewFeature');
 * const maxItems = useFeatureFlag('maxItems', 10);
 * ```
 */
// Passing `true` as defaultValue is a bug: when PostHog disables a flag it returns
// `undefined` (not `false`), so a `true` default makes the flag impossible to turn off.
// Only `false` is allowed for boolean defaults; string/number defaults are for multivariate flags.
export function useFeatureFlag(flagKey: string, defaultValue: InvalidTrueFeatureFlagDefault): never;
export function useFeatureFlag(flagKey: string, defaultValue?: false): boolean;
export function useFeatureFlag<T extends string | number>(flagKey: string, defaultValue: T): T;
export function useFeatureFlag(
    flagKey: string,
    defaultValue?: InvalidTrueFeatureFlagDefault | false | string | number,
): boolean | string | number {
    const variantKey = usePostHogFeatureFlagVariantKey(flagKey);

    // Handle loading state or flag not found
    if (variantKey === undefined) {
        return defaultValue ?? false;
    }

    // For multivariate flags, expect string variants
    if (typeof defaultValue === 'string') {
        // Return the variant key if it's a string, otherwise return default
        return typeof variantKey === 'string' ? variantKey : defaultValue;
    }

    // For boolean flags, variantKey will be true/false
    return variantKey as boolean;
}

export type FeatureFlagSettlement = 'pending' | 'evaluated' | 'timed-out';

type FeatureFlagsSettledOptions = {
    /**
     * PostHog user id (`user.id.toString()`) the flags must have been evaluated for. When given,
     * the hook also waits for `identifyUser()` to finish reloading flags for that user, so
     * person/group-targeted flags are not judged against the anonymous or previous identity.
     */
    userId?: string;
    /**
     * Organisation id (`org.id.toString()`) the flags must have been evaluated in. PostHog
     * evaluates against the organisation group and the same user id spans org memberships, so
     * after an org switch the previous organisation's map must not count. Only meaningful with
     * `userId`.
     */
    orgId?: string;
    /**
     * Upper bound on the unknown state. Defaults to the client's own worst case for the
     * requested signal: 5 s for initialisation alone (matching `waitForFeatureFlags`), 8 s when
     * an identity is awaited — the identify reload (3 s) can only start after initialisation.
     */
    timeoutMs?: number;
};

/**
 * Whether feature-flag evaluation has settled — `initializePostHog()` finished (the SDK holds
 * flags it evaluated itself, or PostHog is unavailable and defaults are final), optionally
 * `identifyUser()` reloaded them for `userId`, or the wait timed out because no response will
 * ever come (quota-limited `/flags`, blocked network). Once a wait has timed out, later mounts
 * awaiting the same identity settle at once until that identity's flags arrive.
 *
 * `useFeatureFlag` / `useAllFeatureFlags` cannot distinguish "flag off" from "flags not loaded
 * yet" — both read as the default. Use this when acting on a `false` flag value is destructive
 * but recoverable (e.g. rendering 404 for a flag-gated route, which re-renders into the page when
 * the flags arrive): treat the flag as unknown until this returns true. A timeout also counts as
 * settled here; where the action cannot be undone (redirecting away), use
 * `useFeatureFlagSettlement` to tell a timeout apart from an evaluation.
 *
 * Deliberately NOT the SDK's `hasLoadedFlags` (CUR-891): a server bootstrap marks flags as
 * loaded while replacing the persisted set with the maintenance registry alone, so every other
 * flag reads as off — and "loaded" — until the forced refresh lands. Readiness comes from
 * `posthogClient`, which only reports `ready` after that refresh.
 */
export function useAreFeatureFlagsSettled(options: FeatureFlagsSettledOptions = {}): boolean {
    return useFeatureFlagSettlement(options) !== 'pending';
}

/**
 * How feature-flag evaluation for the requested identity has settled:
 *
 * - `pending`: still waiting, inside the budget.
 * - `evaluated`: PostHog evaluated flags for that identity, or is unavailable so defaults are
 *   final. A `false` flag value really is off.
 * - `timed-out`: the budget ran out (now or in an earlier mount, see `useAreFeatureFlagsSettled`)
 *   with nothing evaluated. Flag values are still the defaults and may change when a late
 *   response lands, which moves this to `evaluated`.
 *
 * `useAreFeatureFlagsSettled` folds the last two together, which suits a decision that undoes
 * itself when the flags arrive (a 404 that re-renders into the page). Use this hook where acting
 * on a `false` value cannot be undone — a navigation away from a flag-gated route — and handle
 * `timed-out` without that action.
 */
export function useFeatureFlagSettlement({
    userId,
    orgId,
    timeoutMs,
}: FeatureFlagsSettledOptions = {}): FeatureFlagSettlement {
    // With an identity requested, the wait must cover initialisation (≤ 5 s) PLUS the identify
    // reload (≤ 3 s) that can only start after it — a shorter budget would re-paint the
    // 404-that-clears this hook exists to prevent whenever `/flags` answers between 5 s and 8 s.
    const effectiveTimeoutMs = timeoutMs ?? (userId === undefined ? 5000 : 8000);
    const readiness = useSyncExternalStore(
        subscribeFeatureFlagReadiness,
        getFeatureFlagReadiness,
        getFeatureFlagReadiness,
    );
    const isIdentityEvaluated =
        userId === undefined ||
        (readiness.identifiedUserId === userId && (orgId === undefined || readiness.identifiedOrgId === orgId));
    const isSettled =
        readiness.initialisation === 'unavailable' || (readiness.initialisation === 'ready' && isIdentityEvaluated);
    const [hasTimedOut, setHasTimedOut] = useState(false);
    const waitKey = settleWaitKey(effectiveTimeoutMs, userId, orgId);
    // An earlier mount already waited out this identity's budget and nothing has arrived since:
    // a failed `/flags` leaves readiness pending for the rest of the document, so every page that
    // mounts again (the Claims list after each visit to a claim) would otherwise wait it out anew.
    const hasTimedOutBefore = !isSettled && timedOutSettleWaits.has(waitKey);

    // The timeout bounds each UNSETTLED episode, not the component's lifetime: readiness can go
    // back to pending later (re-identification after an org switch) and that episode deserves
    // its own wait rather than inheriting a timeout that fired minutes ago. An identity whose
    // flags do arrive is forgotten, so its next episode waits again.
    useEffect(() => {
        if (isSettled) {
            timedOutSettleWaits.delete(waitKey);
            setHasTimedOut(false);
            return;
        }
        if (timedOutSettleWaits.has(waitKey)) return;
        const timeoutId = setTimeout(() => {
            timedOutSettleWaits.add(waitKey);
            setHasTimedOut(true);
        }, effectiveTimeoutMs);
        return () => clearTimeout(timeoutId);
    }, [isSettled, effectiveTimeoutMs, waitKey]);

    if (isSettled) return 'evaluated';
    return hasTimedOut || hasTimedOutBefore ? 'timed-out' : 'pending';
}

/** Waits that timed out in this document, by budget and identity (see `useAreFeatureFlagsSettled`). */
const timedOutSettleWaits = new Set<string>();

function settleWaitKey(timeoutMs: number, userId: string | undefined, orgId: string | undefined): string {
    return JSON.stringify([timeoutMs, userId ?? null, orgId ?? null]);
}

/** Forget the timed-out waits — module state shared by every hook instance in the document. */
export function resetFeatureFlagSettleWaitsForTests(): void {
    timedOutSettleWaits.clear();
}

/**
 * Hook to get multiple feature flags at once
 *
 * @param flagKeys - Array of feature flag keys
 * @returns Object with flag keys and their values
 *
 * @example
 * ```typescript
 * const { enableAi, enableDebug, maxItems } = useFeatureFlags([
 *   'enableAi',
 *   'enableDebug',
 *   'maxItems'
 * ]);
 * ```
 */
export function useFeatureFlags<K extends string>(flagKeys: K[]): Record<K, boolean | string | number | undefined> {
    const posthog = usePostHog();
    return useMemo(() => {
        if (!posthog) {
            return {} as Record<K, boolean | string | number | undefined>;
        }
        return flagKeys.reduce(
            (acc, key) => {
                acc[key] = posthog.getFeatureFlag(key) as boolean | string | number | undefined;
                return acc;
            },
            {} as Record<K, boolean | string | number | undefined>,
        );
    }, [posthog, flagKeys]);
}

/**
 * Hook to get all feature flags
 *
 * Note: This returns all flags which may include flags not used in your component.
 * Prefer useFeatureFlag or useFeatureFlags for better performance.
 *
 * @returns Object with all flag keys and their values
 */
export function useAllFeatureFlags(): Record<string, unknown> {
    const posthog = usePostHog();
    // `getFlagVariants()` builds a fresh object on every call, which would make
    // every consumer (and anything memoised against it) recompute on every
    // render — `useHasRouteAccess` alone is used by the route root, the guards,
    // the tabs and both navigations. Hold the last map and only swap it when a
    // shallow key/value comparison actually differs, so neither serialisation
    // nor key ordering enters the identity.
    const cachedRef = useRef<Record<string, unknown>>(EMPTY_FLAGS);
    // Subscribe to flag deliveries (CUR-891): without this, a guard or nav that read an
    // incomplete map on mount (e.g. right after a server bootstrap replaced the persisted
    // flags) only re-evaluated when something unrelated happened to re-render it. Both
    // callbacks are kept stable against the client so useSyncExternalStore does not churn
    // a PostHog unsubscribe/resubscribe cycle on every consumer render.
    const subscribe = useCallback(
        (onStoreChange: () => void) => posthog?.onFeatureFlags?.(() => onStoreChange()) ?? (() => {}),
        [posthog],
    );
    const getSnapshot = useCallback(() => {
        const variants = (posthog?.featureFlags?.getFlagVariants() ?? EMPTY_FLAGS) as Record<string, unknown>;
        if (!isSameFlagMap(cachedRef.current, variants)) {
            cachedRef.current = variants;
        }
        return cachedRef.current;
    }, [posthog]);
    return useSyncExternalStore(subscribe, getSnapshot, getEmptyFlags);
}

const EMPTY_FLAGS: Record<string, unknown> = {};
const getEmptyFlags = () => EMPTY_FLAGS;

/** Shallow key/value equality — flag values are primitives (boolean | string | number). */
function isSameFlagMap(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
    if (a === b) {
        return true;
    }
    const aKeys = Object.keys(a);
    if (aKeys.length !== Object.keys(b).length) {
        return false;
    }
    return aKeys.every((key) => Object.hasOwn(b, key) && a[key] === b[key]);
}

/**
 * Hook to check if a feature flag is enabled (boolean only)
 *
 * This is a convenience hook for boolean flags.
 *
 * @param flagKey - The key of the feature flag
 * @returns True if the flag is enabled, false otherwise
 */
export function useFeatureFlagEnabled(flagKey: string): boolean {
    return useFeatureFlag(flagKey, false);
}
